import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetWidgetAttribute } from "./useSetWidgetAttribute.js";
import { addHiddenItem, cloneWidget, createWidget, deleteWidget, saveLayout, saveWidgetSettings, saveWidgetSettingsWithId } from "../redux/dashboardSlice.js";

export const useTabContainer = ({widgetId}) => {
  const dispatch = useDispatch();
  const setAttribute = useSetWidgetAttribute(widgetId);
  const [activeTab, setActiveTab] = useState(null);
  const widgets = useSelector(state => state.dashboard.widgets);
  const gridStoreLayout = useSelector(state => state.dashboard.layout);
  const tabs = useSelector(state => state.dashboard.widgets[widgetId].settings?.tabs || []);
  const editingId = useSelector(state => state.dashboard.editingId);
  const activeWidgetId = tabs.find(t => t.id === activeTab)?.widgetId || null;
  const isEditing = editingId === activeWidgetId;

  useEffect(() => {
    const currentTab = tabs.find(t => t.id === activeTab);
    if (!currentTab) {
      setActiveTab(tabs[0]?.id ?? null);
    }
  }, [tabs, activeTab, widgets, gridStoreLayout]);

  const setTabWidget = useCallback(async newWidgetId => {
    const newTabs = cloneDeep(tabs).map(t => ({
      ...t,
      ...(activeTab === t.id && { widgetId: newWidgetId }),
    }));
    setAttribute('settings.tabs', newTabs);
  }, [tabs, activeTab, setAttribute]);
  
  const onDrop = useCallback(async (layout, layoutItem, event) => {
    try {
      const type = event.dataTransfer.getData('widget-type');
      if (type === 'TabContainerWidget') {
        alert(I18n.t('tab_container_widget_nested_warning'));
        return;
      }
      const existingId = event.dataTransfer.getData('existing-widget-id');
      const result = existingId
        ? await dispatch(cloneWidget({widgetId: existingId, layoutItem}))
        : await dispatch(createWidget({type, layoutItem}));

      const newWidgetId = result.payload.widget.id;
      setTabWidget(newWidgetId);
      await dispatch(addHiddenItem(newWidgetId))
      await dispatch(saveLayout());
      await dispatch(saveWidgetSettingsWithId({widgetId}));
    } catch (e) {
      alert(I18n.t('widget_creation_failed'));
    }
  }, [dispatch, setTabWidget, widgetId]);
  
  const moveTab = useCallback((draggedIndex, targetIndex) => {
    const selectedTabs = cloneDeep(tabs)
    const [d] = selectedTabs.splice(draggedIndex, 1);
    selectedTabs.splice(targetIndex, 0, d);
    setAttribute('settings.tabs', selectedTabs)
  }, [tabs]);

  const removeTab = useCallback(async (id) => {
    const tab = tabs.find(t => t.id === id);
    if (tab.widgetId && !confirm(I18n.t('confirm_delete_widget_tab'))) {
      return;
    }
    const newTabs = tabs.filter(s => s.id !== id);
    setAttribute('settings.tabs', newTabs);

    if (tab?.widgetId) {
      await dispatch(deleteWidget(tab.widgetId));
      await dispatch(saveWidgetSettings());
    };
  }, [tabs, dispatch]);

  const addTab = useCallback(() => {
    const newTabs = [
      ...tabs,
      {id: StringHelper.random(8), name: null, widgetId: null}
    ];
    setAttribute('settings.tabs', newTabs);
  }, [tabs]);

  const activeWidget = useMemo(() => {
    if (!activeWidgetId) return null;

    if (!gridStoreLayout[activeWidgetId]) return null;

    return widgets[activeWidgetId] || null;
  }, [activeWidgetId, widgets, gridStoreLayout]);

  const layout = useMemo(() => (activeWidgetId
    ? [{ h: 1, w: 1, x: 0, y: 0, i: `widget-${activeWidgetId}` }]
    : []
  ), [activeWidgetId]);

  return {
    onDrop,
    activeWidget,
    layout,
    isEditing,
    activeTab,
    setActiveTab,
    moveTab,
    removeTab,
    addTab
  }
}